import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, MessageCircle, Heart, Sparkles, Eye, ShieldCheck, ShieldAlert, BellRing } from "lucide-react";
import SwipeDeck from "@/components/SwipeDeck";
import MatchesPanel from "@/components/MatchesPanel";
import ChatView from "@/components/ChatView";
import MatchOverlay from "@/components/MatchOverlay";
import AdmirersPanel from "@/components/AdmirersPanel";
import NoteComposer from "@/components/NoteComposer";
import AccountMenu from "@/components/AccountMenu";
import ProfileStrength from "@/components/ProfileStrength";
import { notificationState, requestNotificationPermission, showNotification } from "@/lib/notifications";

export default function Home() {
  const { user, logout, refresh } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchOverlay, setMatchOverlay] = useState(null);
  const [mobileTab, setMobileTab] = useState("discover"); // discover | admirers | matches | chat
  const [rightTab, setRightTab] = useState("matches"); // matches | admirers (desktop right column)
  const [noteFor, setNoteFor] = useState(null);
  const [unread, setUnread] = useState({}); // matchId -> count
  const lastSeenRef = React.useRef({}); // matchId -> last_message_at iso

  const billing = user?.billing || {};
  const qplus = billing.quietplus;
  const limit = billing.daily_like_limit; // null if unlimited
  const used = billing.daily_likes_used || 0;
  const likesLeft = limit == null ? null : Math.max(0, limit - used);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/discover");
      setCandidates(data);
    } catch (e) {
      toast.error("Couldn't load profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleVerifiedOnly = async () => {
    const next = !(user?.verified_only_filter ?? true);
    try {
      await api.put("/me", { verified_only_filter: next });
      await refresh();
      toast.success(next ? "Showing verified only" : "Showing all profiles");
      loadCandidates();
    } catch {
      toast.error("Couldn't update filter");
    }
  };

  const loadMatches = useCallback(async () => {
    try {
      const { data } = await api.get("/matches");
      // Detect newly arrived messages per match (from the other party)
      const meId = user?.id;
      const newUnread = { ...unread };
      let toastForMatch = null;
      data.forEach((m) => {
        const lastIso = m.last_message?.created_at;
        const lastSenderIsMe = m.last_message?.sender_id === meId;
        const prevSeen = lastSeenRef.current[m.id];
        if (lastIso && prevSeen && lastIso > prevSeen && !lastSenderIsMe && selectedMatch?.id !== m.id) {
          newUnread[m.id] = (newUnread[m.id] || 0) + 1;
          toastForMatch = m;
        }
        if (lastIso) lastSeenRef.current[m.id] = lastIso;
      });
      if (toastForMatch) {
        toast.message(`New from ${toastForMatch.with_user.name}`, {
          description: toastForMatch.last_message.text.slice(0, 90),
          action: { label: "Open", onClick: () => openMatch(toastForMatch) },
        });
        showNotification(
          `${toastForMatch.with_user.name} sent a message`,
          toastForMatch.last_message.text,
          () => openMatch(toastForMatch)
        );
      }
      setUnread(newUnread);
      setMatches(data);
    } catch (e) {
      console.warn("loadMatches failed:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, selectedMatch?.id]);

  useEffect(() => {
    loadCandidates();
    loadMatches();
    // Light polling for unread updates while on /app
    const id = setInterval(() => loadMatches(), 20000);
    return () => clearInterval(id);
  }, [loadCandidates, loadMatches]);

  const performSwipe = async (target, action, note) => {
    setCandidates((prev) => prev.filter((c) => c.id !== target.id));
    try {
      const body = { target_id: target.id, action };
      if (note) body.note = note;
      const { data } = await api.post("/swipe", body);
      await refresh(); // update billing counters
      if (data.match) {
        setMatchOverlay({ withUser: data.with_user, matchId: data.match_id });
        loadMatches();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "like_limit") {
        toast.error(detail.message || "Out of likes today");
        // Put the card back at the front
        setCandidates((prev) => [target, ...prev]);
      } else if (detail?.error === "no_note_credits") {
        toast.error(detail.message);
        setCandidates((prev) => [target, ...prev]);
      } else {
        toast.error("Swipe failed");
      }
    }
    if (candidates.length < 3) loadCandidates();
  };

  const handleSwipe = async (target, action) => {
    await performSwipe(target, action, null);
  };

  const handleNote = (target) => {
    setNoteFor(target);
  };

  const sendNote = async (text) => {
    if (!noteFor) return;
    const target = noteFor;
    setNoteFor(null);
    await performSwipe(target, "like", text);
  };

  const [notifState, setNotifState] = useState(() => notificationState());
  const askNotifications = async () => {
    const r = await requestNotificationPermission();
    setNotifState(r);
    if (r === "granted") toast.success("We'll let you know when something happens");
    else if (r === "denied") toast.error("Notifications blocked in your browser settings");
  };

  const openMatch = (m) => {
    setSelectedMatch(m);
    setMobileTab("chat");
    setUnread((u) => {
      if (!u[m.id]) return u;
      const { [m.id]: _drop, ...rest } = u;
      return rest;
    });
  };

  return (
    <div className="min-h-screen grain-bg flex flex-col" data-testid="home-page">
      {/* Top bar */}
      <header className="px-5 sm:px-10 py-4 flex items-center justify-between border-b border-[#E5DFD3] bg-[#FAF7F2]/80 backdrop-blur sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <span className="font-serif italic text-2xl text-[#913831]" data-testid="home-brand">rendezvous</span>
          <span className="hidden md:inline-block text-xs uppercase tracking-[0.25em] text-[#737373] border-l border-[#E5DFD3] pl-3 ml-1">
            for {user?.name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {qplus ? (
            <Link to="/billing" data-testid="quietplus-badge"
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#1A1818] text-white rounded-full px-3.5 py-1.5 text-xs font-medium hover:bg-[#3a3a3a]">
              <Sparkles size={12} className="text-[#D4A373]" /> Quiet+
            </Link>
          ) : (
            <Link to="/billing" data-testid="upgrade-btn"
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#F3E8DD] border border-[#D4A373]/40 hover:bg-[#EADBC8] text-[#1A1818] rounded-full px-3.5 py-1.5 text-xs font-medium">
              <Sparkles size={12} className="text-[#913831]" /> Upgrade
            </Link>
          )}
          {user?.verified ? (
            <span data-testid="header-verified" title="Identity verified"
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#913831] text-white rounded-full px-3.5 py-1.5 text-xs font-medium">
              <ShieldCheck size={12} /> Verified
            </span>
          ) : (
            <Link to="/verify" data-testid="header-verify-cta"
              className="hidden sm:inline-flex items-center gap-1.5 border border-[#913831]/40 text-[#913831] hover:bg-[#F3E8DD] rounded-full px-3.5 py-1.5 text-xs font-medium">
              <ShieldAlert size={12} /> Verify
            </Link>
          )}
          {likesLeft != null && (
            <span className="hidden sm:inline text-xs text-[#737373] font-medium" data-testid="likes-counter">
              {likesLeft} likes left
            </span>
          )}
          <AccountMenu />
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex flex-col md:flex-row max-w-7xl w-full mx-auto">
        {/* Mobile tabs */}
        <div className="md:hidden flex border-b border-[#E5DFD3] bg-white/60 text-xs" data-testid="mobile-tabs">
          {[
            ["discover", "Discover"],
            ["admirers", "Admirers"],
            ["matches", `Chats${matches.length > 0 ? ` (${matches.length})` : ""}${Object.values(unread).reduce((a,b)=>a+b,0) > 0 ? " •" : ""}`],
          ].map(([k, label]) => (
            <button key={k} data-testid={`mtab-${k}`} onClick={() => setMobileTab(k)}
              className={`flex-1 py-3 font-medium ${mobileTab === k ? "text-[#913831] border-b-2 border-[#913831]" : "text-[#737373]"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Discover (left col) */}
        <section className={`${mobileTab === "discover" ? "flex" : "hidden"} md:flex md:w-[440px] flex-col items-center justify-start p-5 sm:p-8 md:border-r md:border-[#E5DFD3]`}
                 data-testid="discover-panel">
          <div className="w-full max-w-sm">
            <div className="mb-5 flex items-baseline justify-between">
              <h2 className="font-serif text-3xl text-[#1A1818]">Today&apos;s<br /><em className="text-[#913831]">introductions</em></h2>
              <span className="text-xs uppercase tracking-[0.2em] text-[#737373]">{candidates.length} left</span>
            </div>

            {!user?.verified && (
              <Link to="/verify" data-testid="verify-banner"
                className="mb-4 flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-[#D4A373]/40 hover:bg-[#F3E8DD] transition-colors">
                <ShieldAlert size={16} className="text-[#913831] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1A1818]">Get verified — earn more yeses</div>
                  <div className="text-xs text-[#737373]">Add the verified badge in two minutes.</div>
                </div>
                <span className="text-xs uppercase tracking-[0.2em] text-[#913831]">Start</span>
              </Link>
            )}

            {notifState === "default" && (
              <button data-testid="enable-notifications" onClick={askNotifications}
                className="mb-4 w-full flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-[#E5DFD3] hover:bg-[#F0EBE1] transition-colors text-left">
                <BellRing size={16} className="text-[#913831] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1A1818]">Get notified instantly</div>
                  <div className="text-xs text-[#737373]">Pings when someone matches or messages you.</div>
                </div>
                <span className="text-xs uppercase tracking-[0.2em] text-[#913831]">Allow</span>
              </button>
            )}

            <ProfileStrengthGate user={user} />

            <button
              data-testid="verified-filter-toggle"
              onClick={toggleVerifiedOnly}
              className={`mb-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-colors ${
                (user?.verified_only_filter ?? true)
                  ? "bg-[#913831] text-white border-[#913831]"
                  : "bg-white text-[#737373] border-[#E5DFD3] hover:bg-[#F0EBE1]"
              }`}
            >
              <ShieldCheck size={11} />
              Verified only
            </button>

            <SwipeDeck
              candidates={candidates}
              onSwipe={handleSwipe}
              onNote={handleNote}
              loading={loading}
              onRefresh={loadCandidates}
            />
          </div>
        </section>

        {/* Right column: tabs (matches | admirers) + content */}
        <section className={`${mobileTab !== "discover" ? "flex" : "hidden"} md:flex flex-1 min-h-[60vh] flex-col md:flex-row`}>
          {/* Sub-tabs on desktop */}
          <div className={`${selectedMatch && mobileTab === "chat" ? "hidden md:flex" : "flex"} md:w-72 lg:w-80 border-r border-[#E5DFD3] bg-white/40 flex-col`}>
            <div className="hidden md:flex border-b border-[#E5DFD3]">
              <button data-testid="rtab-matches" onClick={() => setRightTab("matches")}
                className={`flex-1 py-3 text-xs uppercase tracking-[0.2em] ${rightTab === "matches" ? "text-[#913831] border-b-2 border-[#913831]" : "text-[#737373]"}`}>
                <MessageCircle size={12} className="inline mr-1" /> Chats
              </button>
              <button data-testid="rtab-admirers" onClick={() => setRightTab("admirers")}
                className={`flex-1 py-3 text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-1 ${rightTab === "admirers" ? "text-[#913831] border-b-2 border-[#913831]" : "text-[#737373]"}`}>
                <Eye size={12} /> Admirers
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              {(mobileTab === "admirers" || (rightTab === "admirers" && mobileTab !== "matches" && mobileTab !== "chat")) ? (
                <AdmirersPanel isQuietPlus={qplus} onAction={(d) => {
                  refresh(); loadMatches();
                  if (d.match) setMatchOverlay({ withUser: d.with_user, matchId: d.match_id });
                }} />
              ) : (
                <MatchesPanel
                  matches={matches}
                  selectedId={selectedMatch?.id}
                  unread={unread}
                  onSelect={openMatch}
                />
              )}
            </div>
          </div>
          <div className={`${selectedMatch ? "flex" : "hidden md:flex"} flex-1 flex-col`}>
            {selectedMatch ? (
              <ChatView
                key={selectedMatch.id}
                match={selectedMatch}
                onBack={() => { setSelectedMatch(null); setMobileTab("matches"); }}
                onLeave={() => {
                  setSelectedMatch(null);
                  setMobileTab("matches");
                  loadMatches();
                  loadCandidates();
                }}
              />
            ) : (
              <EmptyChat />
            )}
          </div>
        </section>
      </div>

      <NoteComposer
        open={!!noteFor}
        target={noteFor}
        billing={billing}
        onClose={() => setNoteFor(null)}
        onSend={sendNote}
      />

      {matchOverlay && (
        <MatchOverlay
          me={user}
          withUser={matchOverlay.withUser}
          onClose={() => setMatchOverlay(null)}
          onMessage={() => {
            const m = matches.find((x) => x.id === matchOverlay.matchId)
              || { id: matchOverlay.matchId, with_user: matchOverlay.withUser, last_message: null, created_at: new Date().toISOString() };
            openMatch(m);
            setMatchOverlay(null);
          }}
        />
      )}
    </div>
  );
}

function ProfileStrengthGate({ user }) {
  // Render only if there's room to grow
  if (!user) return null;
  const photos = user.photos?.length || 0;
  const bio = (user.bio || "").length;
  const interests = user.interests?.length || 0;
  if (photos >= 3 && bio >= 80 && interests >= 4 && user.location && user.verified) return null;
  return <div className="mb-4"><ProfileStrength user={user} compact /></div>;
}

function EmptyChat() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-10 text-[#737373]" data-testid="empty-chat">
      <Heart size={28} strokeWidth={1.25} className="text-[#913831] mb-4" />
      <h3 className="font-serif text-3xl text-[#1A1818] tracking-tight">No conversation yet.</h3>
      <p className="text-sm mt-2 max-w-xs">Connections will appear here once both of you say yes. Take your time on the left.</p>
      <MessageCircle size={20} strokeWidth={1.25} className="mt-8 opacity-40" />
    </div>
  );
}
